/**
 * Column Presets API Client
 * 
 * Handles CRUD operations for column configuration presets.
 * Uses the same apiFetch pattern as the rest of the application.
 */

import { apiFetch } from './api-error-handler';
import { getApiBaseUrl } from './data-service';

export interface ColumnPresetDto {
  id: number;
  userId: string;
  organisationId: string | null;
  name: string;
  pageKey: string;
  columns: string; // JSON string of column config
  isDefault: boolean;
  isShared: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateColumnPresetDto {
  name: string;
  pageKey: string;
  columns: string; // JSON string
  isDefault?: boolean;
  isShared?: boolean;
}

export interface UpdateColumnPresetDto {
  name: string;
  columns: string; // JSON string
  isDefault?: boolean;
  isShared?: boolean;
}

/**
 * Fetch all column presets for the current user on a specific page.
 * Returns the user's own presets + shared presets from the organisation.
 */
export async function fetchColumnPresets(pageKey: string): Promise<ColumnPresetDto[]> {
  return apiFetch<ColumnPresetDto[]>(
    `${getApiBaseUrl()}/columnpresets?pageKey=${encodeURIComponent(pageKey)}`
  );
}

/**
 * Fetch a single column preset by ID.
 */
export async function fetchColumnPresetById(id: number): Promise<ColumnPresetDto> {
  return apiFetch<ColumnPresetDto>(`${getApiBaseUrl()}/columnpresets/${id}`);
}

/**
 * Create a new column preset.
 */
export async function createColumnPreset(preset: CreateColumnPresetDto): Promise<ColumnPresetDto> {
  return apiFetch<ColumnPresetDto>(`${getApiBaseUrl()}/columnpresets`, {
    method: 'POST',
    body: JSON.stringify(preset),
  });
}

/**
 * Update an existing column preset.
 */
export async function updateColumnPreset(id: number, preset: UpdateColumnPresetDto): Promise<void> {
  await apiFetch(`${getApiBaseUrl()}/columnpresets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(preset),
  });
}

/**
 * Delete a column preset.
 */
export async function deleteColumnPreset(id: number): Promise<void> {
  await apiFetch(`${getApiBaseUrl()}/columnpresets/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Set a preset as the default for the current user on its page.
 */
export async function setDefaultColumnPreset(id: number): Promise<void> {
  await apiFetch(`${getApiBaseUrl()}/columnpresets/${id}/set-default`, {
    method: 'PUT',
  });
}
